import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GenericDialog } from "@/components/dialogs";
import { highlight } from "@/util";
import { useDebounce } from "@/hooks";
import { useState } from "react";
import { useWelderLookup } from "../hooks";
import { Welder } from "@/api/williams/welders";

import styles from "./WelderSelection.tsx.module.css";

export type WelderSelectionProps = {
  initialWelders?: Welder[];
  onAccept: (welders: Welder[]) => void;
};

export function WelderSelectionDialog(props: WelderSelectionProps) {
  const [selected, setSelected] = useState<Welder[]>(props.initialWelders ?? []);
  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 300);
  const welders = useWelderLookup(debouncedSearch);

  function isSelected(welder: Welder) {
    return selected.some((x) => x.welderId === welder.welderId);
  }

  function handleSelectionChanged(welder: Welder, checked: boolean) {
    setSelected((prev) => (checked ? [...prev, welder] : prev.filter((x) => x.welderId !== welder.welderId)));
  }

  return (
    <GenericDialog
      caption="Select Welder(s)"
      buttons="OKCancel"
      className={styles.dialog}
      onAccept={() => props.onAccept(selected)}
    >
      <>
        <div className="input-group" style={{ maxWidth: 400 }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <span className="input-group-text" role="button" onClick={() => welders.refetch()}>
            <FontAwesomeIcon size="sm" icon={faMagnifyingGlass} title="Search" />
          </span>
        </div>

        <div className={styles.container}>
          {welders.isLoading ? (
            <p>Loading...</p>
          ) : welders.isError ? (
            <p>An unknown error occurred.</p>
          ) : debouncedSearch && welders.data?.length === 0 ? (
            <p>Your search returned no results.</p>
          ) : welders.data?.length === 0 ? (
            <p>No welders found.</p>
          ) : (
            <table className="table table-sm table-borderless">
              <thead className="table-light">
                <tr>
                  <th>ID Number</th>
                  <th className="d-none d-md-table-cell">Name</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {welders.data?.map((welder) => (
                  <tr key={welder.welderId}>
                    <td>{highlight(welder.badgeNumber, debouncedSearch)}</td>
                    <td className="d-none d-md-table-cell">{highlight(welder.name, debouncedSearch)}</td>
                    <td className="text-end pe-4">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={isSelected(welder)}
                        onChange={(e) => handleSelectionChanged(welder, e.currentTarget.checked)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </>
    </GenericDialog>
  );
}
